// Configuración › Conexiones › Asistente de Amazon (Alexa for Shopping, ex Rufus) — 2026-09-22.
// Estado de la sesión del navegador del servidor + pantalla remota para que UNA PERSONA inicie
// sesión con su teclado + casilla de prueba. Nada de lo que se escribe se guarda en el sistema.
import { useState, useEffect, useCallback, useRef } from 'react';
import { Bot, RefreshCw, MonitorSmartphone, X, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { api } from '../../../api/client';
import { Can } from '../../../lib/rbac';

interface AssistantStatus {
  configured: boolean;
  logged_in: boolean;
  hello?: string | null;
  alexa_available?: boolean;
  interstitial?: boolean;
  last_check_at?: string | null;
  last_ok_at?: string | null;
  last_error?: string | null;
  consecutive_failures?: number;
  asks_total?: number;
  asks_failed?: number;
  last_ask_at?: string | null;
  last_ask_ms?: number | null;
  check_every_min?: number;
}

interface Answer { answer: string; products: string[]; ms: number }

const fmtAgo = (iso?: string | null): string => {
  if (!iso) return 'nunca';
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return 'recién';
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  return h < 48 ? `hace ${h} h` : `hace ${Math.round(h / 24)} días`;
};

const SIGNIN_URL =
  'https://www.amazon.com/ap/signin?openid.return_to=https%3A%2F%2Fwww.amazon.com%2F&openid.identity=http%3A%2F%2Fspecs.openid.net%2Fauth%2F2.0%2Fidentifier_select&openid.assoc_handle=usflex&openid.mode=checkid_setup&openid.claimed_id=http%3A%2F%2Fspecs.openid.net%2Fauth%2F2.0%2Fidentifier_select&openid.ns=http%3A%2F%2Fspecs.openid.net%2Fauth%2F2.0';

const REMOTE_W = 1280;
const REMOTE_H = 800;
const REMOTE_KEYS = new Set(['Enter', 'Backspace', 'Tab', 'ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Escape', 'Delete']);

function RemoteScreen({ onClose }: { onClose: () => void }) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [src, setSrc] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const busy = useRef(false);

  const remote = useCallback((action: string, payload: Record<string, unknown> = {}) =>
    api.post('/settings/amazon-assistant/remote', { action, ...payload }), []);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      if (busy.current || !alive) return;
      busy.current = true;
      try {
        const blob = await api.getBlob(`/settings/amazon-assistant/shot?n=${Date.now()}`);
        if (blob && alive) {
          const url = URL.createObjectURL(blob);
          setSrc((old) => { if (old) URL.revokeObjectURL(old); return url; });
        }
      } catch { /* la próxima captura lo intenta de nuevo */ }
      busy.current = false;
    };
    tick();
    const t = setInterval(tick, 900);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const onClick = (e: React.MouseEvent<HTMLImageElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    remote('click', { x: ((e.clientX - r.left) * REMOTE_W) / r.width, y: ((e.clientY - r.top) * REMOTE_H) / r.height });
    imgRef.current?.focus();
  };
  const onKey = (e: React.KeyboardEvent<HTMLImageElement>) => {
    e.preventDefault();
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) remote('key', { text: e.key });
    else if (REMOTE_KEYS.has(e.key)) remote('key', { key: e.key });
  };
  const onPaste = (e: React.ClipboardEvent<HTMLImageElement>) => {
    e.preventDefault();
    const s = e.clipboardData.getData('text');
    if (s) remote('key', { text: s });
  };
  const onWheel = (e: React.WheelEvent<HTMLImageElement>) => { remote('scroll', { dy: e.deltaY }); };

  const finish = async () => {
    setNote('Verificando la sesión…');
    const res = await api.post<AssistantStatus>('/settings/amazon-assistant/remote', { action: 'remote-done' });
    const s = res.data as AssistantStatus | undefined;
    setNote(s?.logged_in ? `✅ Sesión iniciada (${s.hello})` : `❌ Todavía sin sesión${s?.last_error ? `: ${s.last_error}` : ''}`);
    if (s?.logged_in) setTimeout(onClose, 1500);
  };

  const btn = 'px-2.5 py-1 rounded-md bg-[var(--color-surface-hover)] text-xs text-[var(--color-text)] hover:opacity-80';
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-auto p-4" onClick={onClose}>
      <div className="bg-[var(--color-surface)] rounded-xl border border-[var(--color-border)] w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 p-3 border-b border-[var(--color-border)] flex-wrap">
          <MonitorSmartphone className="w-4 h-4 text-[var(--color-primary)]" />
          <span className="text-sm font-medium text-[var(--color-text)]">Navegador del servidor — iniciar sesión en Amazon</span>
          <div className="flex-1" />
          <button className={btn} onClick={() => remote('goto', { url: SIGNIN_URL })}>Ir a iniciar sesión</button>
          <button className={btn} onClick={() => remote('goto', { url: 'https://www.amazon.com/' })}>Ir a amazon.com</button>
          <button className={btn} onClick={() => remote('key', { key: 'Enter' })}>Enter</button>
          <button className={btn} onClick={() => remote('key', { key: 'Backspace' })}>Borrar</button>
          <button className={btn} onClick={() => remote('key', { key: 'Tab' })}>Tab</button>
          <button className="px-3 py-1 rounded-md bg-[var(--color-primary)] text-white text-xs font-medium hover:opacity-90" onClick={finish}>Listo, verificar</button>
          <button className="p-1 rounded hover:bg-[var(--color-surface-hover)] text-[var(--color-text-muted)]" onClick={onClose} aria-label="Cerrar"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-3">
          {src ? (
            <img
              ref={imgRef}
              src={src}
              alt="Pantalla del navegador del servidor"
              tabIndex={0}
              className="w-full rounded-lg border-2 border-[var(--color-border)] focus:border-[var(--color-primary)] outline-none cursor-crosshair select-none"
              onClick={onClick}
              onKeyDown={onKey}
              onPaste={onPaste}
              onWheel={onWheel}
              draggable={false}
            />
          ) : (
            <div className="h-64 flex items-center justify-center text-sm text-[var(--color-text-muted)]"><RefreshCw className="w-4 h-4 animate-spin mr-2" /> Cargando la pantalla…</div>
          )}
          <p className="text-xs text-[var(--color-text-muted)] mt-2">
            Hacé clic en la imagen donde quieras escribir (por ejemplo, el campo de correo) y escribí con tu teclado. Lo que escribís va directo al
            navegador del servidor: el sistema no lo guarda ni lo muestra. Si Amazon pide un código por correo o celular, escribilo igual.
            Al terminar, tocá “Listo, verificar”.
          </p>
          {note && <p className="text-sm mt-2 text-[var(--color-text)]">{note}</p>}
        </div>
      </div>
    </div>
  );
}

export function AmazonAssistantPanel() {
  const [st, setSt] = useState<AssistantStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [remote, setRemote] = useState(false);
  const [q, setQ] = useState('Is it rated for 110V or 220V? Answer briefly.');
  const [asin, setAsin] = useState('B00NGV4506');
  const [ans, setAns] = useState<string>('');

  const load = useCallback(async () => {
    const res = await api.get<AssistantStatus>('/settings/amazon-assistant/status');
    if (res.success && res.data) setSt(res.data as AssistantStatus);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const check = async () => {
    setBusy(true);
    try {
      const res = await api.post<AssistantStatus>('/settings/amazon-assistant/check', {});
      if (res.success && res.data) setSt(res.data as AssistantStatus);
      else if (res.error) setAns(`Error: ${res.error}`);
    } finally { setBusy(false); }
  };

  const ask = async () => {
    if (!q.trim()) return;
    setBusy(true); setAns('Preguntando…');
    try {
      const res = await api.post<Answer>('/settings/amazon-assistant/ask', { question: q, asin: asin.trim() || undefined });
      const d = res.data as Answer | undefined;
      setAns(res.success && d ? `${d.answer}\n\n(${Math.round(d.ms / 100) / 10} segundos${d.products?.length ? ` · productos citados: ${d.products.join(', ')}` : ''})` : `Error: ${res.error || 'sin respuesta'}`);
    } finally { setBusy(false); load(); }
  };

  const estado = !st
    ? { label: 'Cargando…', color: 'var(--color-text-muted)', Icon: RefreshCw }
    : !st.configured
      ? { label: 'No configurado en este servidor', color: 'var(--color-text-muted)', Icon: XCircle }
      : st.logged_in
        ? { label: `Sesión iniciada · ${st.hello || ''}`, color: 'var(--color-success, #16a34a)', Icon: CheckCircle }
        : st.interstitial
          ? { label: 'Amazon pide un clic para continuar (abrí la pantalla remota)', color: '#d97706', Icon: AlertTriangle }
          : { label: `Sin sesión${st.last_error ? ` · ${st.last_error}` : ''}`, color: '#dc2626', Icon: XCircle };

  return (
    <div className="rounded-lg border border-[var(--color-border)] p-4 bg-[var(--color-surface)] space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Bot className="w-4 h-4" style={{ color: estado.color }} />
          <div>
            <div className="text-sm font-medium text-[var(--color-text)]">Asistente de Amazon (Alexa for Shopping)</div>
            <div className="text-xs" style={{ color: estado.color }}>
              <estado.Icon className="w-3 h-3 inline mr-1 -mt-0.5" />
              {estado.label}
            </div>
            {st?.configured && (
              <div className="text-xs text-[var(--color-text-muted)] mt-0.5">
                Último chequeo {fmtAgo(st.last_check_at)} · última vez con sesión {fmtAgo(st.last_ok_at)} · se revisa cada {st.check_every_min ?? 20} min
                {st.asks_total ? ` · ${st.asks_total} preguntas (${st.asks_failed ?? 0} fallidas)` : ''}
              </div>
            )}
          </div>
        </div>
        <Can cap="settings.amazon_assistant">
          <div className="flex items-center gap-2">
            <button disabled={busy || !st?.configured} onClick={check} className="px-3 py-1.5 rounded-lg bg-[var(--color-surface-hover)] text-[var(--color-text)] text-xs font-medium hover:opacity-90 disabled:opacity-50 flex items-center gap-1">
              <RefreshCw size={14} className={busy ? 'animate-spin' : ''} /> Verificar ahora
            </button>
            <button disabled={!st?.configured} onClick={() => setRemote(true)} className="px-3 py-1.5 rounded-lg bg-[var(--color-primary)] text-white text-xs font-medium hover:opacity-90 disabled:opacity-50 flex items-center gap-1">
              <MonitorSmartphone size={14} /> {st?.logged_in ? 'Abrir pantalla remota' : 'Iniciar sesión'}
            </button>
          </div>
        </Can>
      </div>

      <Can cap="settings.amazon_assistant">
        <div className="border-t border-[var(--color-border)] pt-3 space-y-2">
          <div className="text-xs font-medium text-[var(--color-text-secondary)]">Pregunta de prueba</div>
          <div className="flex gap-2 flex-wrap">
            <input value={asin} onChange={(e) => setAsin(e.target.value.toUpperCase())} placeholder="ASIN (opcional)" maxLength={10}
              className="w-36 px-2 py-1.5 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] text-sm text-[var(--color-text)]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pregunta en inglés (el asistente responde mejor en inglés)"
              className="flex-1 min-w-[240px] px-2 py-1.5 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] text-sm text-[var(--color-text)]"
              onKeyDown={(e) => { if (e.key === 'Enter') ask(); }} />
            <button disabled={busy || !st?.logged_in} onClick={ask} className="px-3 py-1.5 rounded-lg bg-[var(--color-primary)] text-white text-xs font-medium hover:opacity-90 disabled:opacity-50">
              Preguntar
            </button>
          </div>
          {ans && <pre className="whitespace-pre-wrap text-sm text-[var(--color-text)] bg-[var(--color-bg)] rounded-md p-3 border border-[var(--color-border)] font-sans">{ans}</pre>}
        </div>
      </Can>

      {remote && <RemoteScreen onClose={() => { setRemote(false); load(); }} />}
    </div>
  );
}
